import { PlusIcon } from "@heroicons/react/20/solid";
import { ProjectStatusDisplayName } from "pmt-types/src/enums/ProjectStatus.ts";
import { Project } from "pmt-types/src/models/Project.ts";
import dateStringComparer from "../../utils/dateStringComparer.ts";
import formatDateString from "../../utils/formatDateString.ts";
import { Maybe } from "../../utils/types/maybe.ts";

type Props = {
  projects: Maybe<Project[]>;
  onAddProject: () => void;
  onViewProject: (project: Project) => void;
  onEditProject: (project: Project) => void;
  onDeleteProject: (project: Project) => void;
};

export default function SourceProjectList({
  projects,
  onAddProject,
  onViewProject,
  onEditProject,
  onDeleteProject,
}: Props) {
  const hasProjects = projects != null && projects.length > 0;

  return (
    <div>
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-base font-semibold leading-6 text-gray-900">
            Projects
          </h1>
          <p className="mt-2 text-sm text-gray-700">
            {hasProjects
              ? "All the projects for this source."
              : "No projects setup yet."}
          </p>
        </div>
        <div className="mt-4 sm:ml-16 sm:mt-0 sm:flex-none">
          <button
            type="button"
            className="inline-flex items-center rounded-md bg-sky-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-sky-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-600"
            onClick={onAddProject}
          >
            <PlusIcon className="-ml-0.5 mr-1.5 h-5 w-5" aria-hidden="true" />
            Add Project
          </button>
        </div>
      </div>
      {hasProjects && (
        <div className="mt-8 flow-root">
          <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
            <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
              <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead className="bg-gray-50">
                    <tr>
                      <th
                        scope="col"
                        className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6"
                      >
                        Project
                      </th>
                      <th
                        scope="col"
                        className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                      >
                        Status
                      </th>
                      <th
                        scope="col"
                        className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                      >
                        Project Dates
                      </th>
                      <th
                        scope="col"
                        className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                      >
                        Last Updated
                      </th>
                      <th
                        scope="col"
                        className="relative py-3.5 pl-3 pr-4 sm:pr-6"
                      >
                        <span className="sr-only">Edit</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {projects
                      .sort((a, b) =>
                        dateStringComparer(a.updatedDate, b.updatedDate),
                      )
                      .map((project, index) => (
                        <tr key={index}>
                          <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">
                            {project.name}
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                            {ProjectStatusDisplayName[project.status]}
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                            {project.startDate} - {project.endDate}
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                            {formatDateString(project.updatedDate)}
                          </td>
                          <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                            <div className="flex items-center justify-around mr-4">
                              <a
                                onClick={() => onViewProject(project)}
                                className="text-sky-500 hover:text-sky-700 cursor-pointer"
                              >
                                View<span className="sr-only">, {index}</span>
                              </a>
                              <a
                                onClick={() => onEditProject(project)}
                                className="text-sky-500 hover:text-sky-700 cursor-pointer"
                              >
                                Edit<span className="sr-only">, {index}</span>
                              </a>
                              <a
                                onClick={() => onDeleteProject(project)}
                                className="text-sky-500 hover:text-sky-700 cursor-pointer"
                              >
                                Delete<span className="sr-only">, {index}</span>
                              </a>
                            </div>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
